'use memo';

import { useState, useEffect, useMemo } from 'react';
import { AgxLegalDescription } from '@urbanx/agx.ui-components2';
import { AgxLabel } from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { LoadingState } from 'utils/loadingState';
import { isEqual } from 'lodash-es';
import {
  LegalDescriptionModel,
  ParcelDetails,
} from '@urbanx/agx.campaigns.client';

interface LegalDescriptionProps {
  id: string;
  label?: string;
  readonly: boolean;
  legalDescription: LegalDescriptionModel;
  onChange: (change: { id: string; value: LegalDescriptionModel }) => void;
  required?: boolean;
  validate?: boolean;
}

const LegalDescriptionElement: React.FC<LegalDescriptionProps> = ({
  id,
  label,
  readonly,
  legalDescription,
  onChange,
  required,
  validate,
}) => {
  const {
    loadingState: titleReferenceLoadingState,
    hasInvalidTitleReference,
    items: titleReferenceItems,
  } = useAppSelector(state => state.rpData.titleReference);

  const [localLegalDescription, setLocalLegalDescription] =
    useState<LegalDescriptionModel>(legalDescription);

  const hasLocalParcels = !!localLegalDescription?.parcels?.length;
  const isLoading =
    titleReferenceLoadingState === LoadingState.Loading && !hasLocalParcels;
  const isDataReady =
    titleReferenceLoadingState === LoadingState.Loaded || hasLocalParcels;

  const constructedLegalDescription = useMemo(() => {
    if (!isDataReady || !localLegalDescription?.parcels) {
      return localLegalDescription;
    }

    const updatedParcels = localLegalDescription.parcels.map(
      (parcel: ParcelDetails, index: number) => {
        return {
          ...parcel,
          lotNumber: parcel.lotNumber || '',
          planType: parcel.planType || '',
          planNumber: parcel.planNumber || '',
          titleReference: parcel.titleReference || '',
        };
      }
    );

    return {
      ...localLegalDescription,
      parcels: updatedParcels,
    };
  }, [localLegalDescription.parcels, isDataReady]);

  useEffect(() => {
    if (!isEqual(legalDescription, localLegalDescription)) {
      setLocalLegalDescription(legalDescription);
    }
  }, [legalDescription.parcels]);

  const updateParcels = (parcels: ParcelDetails[]) => {
    const updatedLegalModel = {
      ...constructedLegalDescription,
      parcels: [...parcels],
    };

    setLocalLegalDescription(updatedLegalModel);
    onChange({ id, value: updatedLegalModel });
  };

  const onChangeParcel = (index: number, newParcel: ParcelDetails) => {
    if (!constructedLegalDescription?.parcels) return;

    const updatedParcels = constructedLegalDescription.parcels.map(
      (parcel, parcelIndex) => (index === parcelIndex ? newParcel : parcel)
    );

    if (isEqual(updatedParcels, constructedLegalDescription.parcels)) return;
    updateParcels(updatedParcels);
  };

  const onRemoveParcel = (index: number) => {
    if (!constructedLegalDescription?.parcels) return;

    const parcelToRemove = constructedLegalDescription.parcels[index];
    updateParcels([
      ...constructedLegalDescription.parcels.filter(
        (_: ParcelDetails, parcelIndex: number) => index !== parcelIndex
      ),
    ]);
  };

  const onAddNewParcel = () => {
    const newParcel: ParcelDetails = {
      isPrimaryPlan: false,
      lotNumber: '',
      planType: '',
      planNumber: '',
      titleReference: '',
    };

    updateParcels([...(constructedLegalDescription?.parcels ?? []), newParcel]);
  };

  if (isLoading || !isDataReady) {
    return (
      <AgxLabel medium dataTestId={`agx-TitleRef-loading`}>
        Loading...
      </AgxLabel>
    );
  }

  return (
    <AgxLegalDescription
      id={id}
      key={id}
      label={label}
      hasInvalidTitleReference={hasInvalidTitleReference}
      readonly={readonly}
      legalDescription={constructedLegalDescription}
      onChangeParcel={onChangeParcel}
      onRemoveParcel={onRemoveParcel}
      onAddNewParcel={onAddNewParcel}
      required={required}
      validate={validate}
    />
  );
};

export default LegalDescriptionElement;
